import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import type { GridProps } from '@mui/material/Grid';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip, { type TooltipProps } from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { useRHFNestedFormContext } from '../Form/context/RHFNestedFormContext';

type FormSectionProps = {
  children?: ReactNode;
  customGrid?: boolean;
  'data-test'?: string;
  description?: ReactNode;
  divider?: boolean;
  historyMode?: boolean;
  info?: ReactNode;
  leftColumnSize?: GridProps['size'];
  rightColumnSize?: GridProps['size'];
  title: string;
  titleIcon?: ReactNode;
  tooltipPlacement?: TooltipProps['placement'];
  name?: string;
  id?: string;
};

export function FormSection(props: FormSectionProps): ReactNode {
  const {
    children,
    customGrid,
    description,
    divider,
    historyMode = false,
    info,
    leftColumnSize = 3,
    rightColumnSize = 9,
    title,
    titleIcon,
    tooltipPlacement = 'top',
    name,
  } = props;
  const { isSectionVisible, getSectionConfig } = useRHFNestedFormContext();

  if (!isSectionVisible(name)) {
    return null;
  }

  const sectionConfig = getSectionConfig(name);

  if (sectionConfig?.contentOnly) {
    return <>{children}</>;
  }

  const dataTest = props['data-test'] || title.toLowerCase().replace(/\s/g, '-');

  if (historyMode) {
    return (
      <>
        <Typography
          data-test="group-title"
          variant="h3"
          sx={{
            mb: 1.5,
            pt: 1,
            '&:first-of-type': { pt: 0 },
          }}
        >
          {title}
        </Typography>
        {children}
      </>
    );
  }

  return (
    <Box
      data-test={dataTest}
      id={props.id}
      sx={{
        width: '100%',
        visibility: sectionConfig?.hidden ? 'hidden' : 'visible',
      }}
    >
      <Grid
        container
        sx={{
          p: 4,
        }}
      >
        <Grid size={leftColumnSize}>
          <Box
            sx={{
              pr: 3,
            }}
          >
            <Typography
              data-test="title"
              variant="h2"
              sx={{
                mb: 1,

                ':before': {
                  borderTopColor: 'primary.main',
                  borderTopStyle: 'solid',
                  borderTopWidth: '1px ',
                  content: '""',
                  display: 'block',
                  marginBottom: '8px',
                  width: '12px',
                },
              }}
            >
              <Box
                component={'span'}
                sx={{
                  display: 'flex',
                }}
              >
                {titleIcon ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: 'center',
                    }}
                  >
                    {title}
                    {titleIcon}
                  </Stack>
                ) : (
                  title
                )}
                {info && (
                  <Tooltip placement={tooltipPlacement} sx={{ ml: '10px' }} title={info}>
                    <IconButton size="small">
                      <InfoRoundedIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Typography>

            {description && (
              <Typography
                color="textSecondary"
                component="div"
                data-test="description"
                sx={{ wordBreak: 'break-word' }}
                variant="body1"
              >
                {description}
              </Typography>
            )}
          </Box>
        </Grid>
        {!customGrid ? <Grid size={rightColumnSize}>{children}</Grid> : children}
      </Grid>

      {divider && <Divider />}
    </Box>
  );
}
